import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { db } from "@/services/firebase";
import { testimonials as STATIC_FALLBACK } from "@/data";
import type { Testimonial } from "@/types";

/**
 * DRB-64 — the homepage testimonials, from Firestore.
 *
 * They were rendered from a hardcoded array in src/data/testimonials.ts while the ADMIN managed a
 * Firestore collection. Two different sources: nothing she added in the admin ever appeared on the
 * site, and the four quotes actually on the site could not be edited or removed. The Firestore
 * collection was EMPTY, so her Testimonials screen managed nothing at all — a control that
 * persists but that nothing reads is a stub, however complete it looks.
 *
 * The query MUST carry where("verified","==",true): firestore.rules gates public read on that
 * field, and Firestore denies an entire query unless its constraints guarantee every returned
 * document satisfies the rule. An unconstrained read here fails as a permission error, not as a
 * partial result.
 */
export function useTestimonials() {
  const [items, setItems] = useState<Testimonial[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, "testimonials"),
      where("verified", "==", true),
      orderBy("order"),
    );
    return onSnapshot(
      q,
      (snap) => {
        setItems(
          snap.docs.map((d) => ({ ...d.data(), id: d.id }) as Testimonial),
        );
        setFailed(false);
      },
      (err) => {
        // Fall back to the shipped copy ONLY on a real failure, and say so. An empty collection is
        // NOT a failure — if she deletes them all, the section should honour that rather than
        // silently resurrecting quotes she removed.
        console.error(
          "[testimonials] read failed, showing the built-in copy",
          err,
        );
        setFailed(true);
      },
    );
  }, []);

  if (failed)
    return { testimonials: STATIC_FALLBACK, loading: false, degraded: true };
  return {
    testimonials: items ?? [],
    loading: items === null,
    degraded: false,
  };
}
